import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import { printRetrieveInitData } from './tdcf-form.action';
import { TranslationKeyMap } from './tdcf-form.i18n';
import {
    ManagerReportState,
    PrintPreviewState,
    storeStateMapper,
} from './tdcf-form.state';
import { lastModifiedFor, reviewDateRangeFor } from './tdcf.model';

@Component({
    selector: '[cmpsTdcfPrintPreview]',
    templateUrl: './print-preview.tdcf.component.html',
    host: {
        class: 'cmps-tdcf-print-preview',
    },
})
export class PrintPreviewTdcfComponent
    implements OnInit, OnDestroy, StateToProps<PrintPreviewState>
{
    i18n = TranslationKeyMap;
    private _storeSub: Subscription;
    state: PrintPreviewState;
    private _routeSub: Subscription;

    name: string;
    manager: string;
    quarter: string;
    lastModified: Date;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _activatedRoute: ActivatedRoute,
        @Inject(STORE) private readonly _store: Store,
        private readonly _datePipe: CmpsDatePipe,
    ) {}

    ngOnInit(): void {
        this._storeSub = storeObserver<ManagerReportState>(
            this._store,
            (state) => storeStateMapper(state).printPreview,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        this.state = state;
                        this.stateToProps();
                    }),
                ),
            )
            .subscribe();

        this._routeSub = this._activatedRoute.queryParams
            .pipe(
                // eslint-disable-next-line no-prototype-builtins
                filter(
                    (queryParams) =>
                        // eslint-disable-next-line no-prototype-builtins
                        queryParams.hasOwnProperty('tpl') &&
                        // eslint-disable-next-line no-prototype-builtins
                        queryParams.hasOwnProperty('data'),
                ),
                tap((queryParams) => {
                    const templateId = queryParams.tpl;
                    const dataId = queryParams.data;
                    this._store.dispatch(
                        printRetrieveInitData(templateId, dataId),
                    );
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this._routeSub) {
            this._routeSub.unsubscribe();
        }
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(): void {
        this.name = null;
        this.manager = null;
        this.quarter = null;
        this.lastModified = null;

        if (!this.state.metadata || !this.state.payload) return;

        this.name = `${this.state.payload.memberInfo.firstName} ${this.state.payload.memberInfo.lastName}`;
        this.manager = `${this.state.payload.managerInfo.firstName} ${this.state.payload.managerInfo.lastName}`;
        const dr = reviewDateRangeFor(
            this.state.payload.created as Date,
            this.state.metadata.reviewDateRanges,
        );
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        this.quarter = `${dr.displayName} (${this._datePipe.transform(
            dr.start,
            'MMMM dd, y',
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        )} - ${this._datePipe.transform(dr.end, 'MMMM dd, y')})`;
        this.lastModified = lastModifiedFor([
            this.state.payload.created as Date,
            this.state.payload.lastModified as Date,
        ]);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onPrintClick() {
        window.print();
    }
}
